import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';

export interface GeoPoint {
  lat: number;
  lng: number;
}

interface CacheEntry {
  lat: number;
  lng: number;
  ts: number; // tidspunkt (ms) for når adressen ble geokodet
}

const STORAGE_KEY = 'geocode_cache_v1';
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 dager
const MAX_ENTRIES = 5000; // LRU-grense mot ubegrenset vekst

/**
 * Geokoder norske adresser til lat/long via Kartverkets Geonorge-API
 * (gratis, ingen API-nøkkel).
 *
 * Caching:
 *  - In-memory (per økt) + deduplisering av samtidige kall.
 *  - Persistent på enheten (Ionic storage) med 90-dagers TTL, slik at samme
 *    adresse ikke geokodes på nytt på tvers av økter.
 *  - LRU-grense (MAX_ENTRIES) rydder de eldste når cachen blir for stor.
 *
 * Bruker fetch (ikke HttpClient) for å unngå at appens auth-interceptor legger
 * JWT på det eksterne kallet.
 */
@Injectable({ providedIn: 'root' })
export class GeocodingService {
  private memory = new Map<string, GeoPoint | null>();      // inkl. null for feil (kun denne økta)
  private persistent = new Map<string, CacheEntry>();       // speiler det som lagres på enheten
  private inflight = new Map<string, Promise<GeoPoint | null>>();
  private ready: Promise<void>;

  constructor(private storage: Storage) {
    this.ready = this.init();
  }

  private async init(): Promise<void> {
    try {
      await this.storage.create();
      const raw = (await this.storage.get(STORAGE_KEY)) as { [k: string]: CacheEntry } | null;
      if (raw) {
        const now = Date.now();
        let dropped = false;
        for (const key of Object.keys(raw)) {
          const e = raw[key];
          if (e && typeof e.lat === 'number' && now - e.ts < TTL_MS) {
            this.persistent.set(key, e);
            this.memory.set(key, { lat: e.lat, lng: e.lng });
          } else {
            dropped = true; // utløpt/ugyldig
          }
        }
        if (dropped) {
          await this.persist(); // rydd bort utløpte ved oppstart
        }
      }
    } catch {
      // Storage utilgjengelig — fortsett med kun in-memory cache
    }
  }

  async geocode(address: string): Promise<GeoPoint | null> {
    const key = (address || '').trim().toLowerCase();
    if (!key) {
      return null;
    }
    await this.ready;

    if (this.memory.has(key)) {
      return this.memory.get(key) as GeoPoint | null;
    }
    if (this.inflight.has(key)) {
      return this.inflight.get(key) as Promise<GeoPoint | null>;
    }

    const url =
      'https://ws.geonorge.no/adresser/v1/sok?sok=' +
      encodeURIComponent(address.trim()) +
      '&treffPerSide=1&utkoordsys=4326';

    const request = fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then(async (res: any) => {
        const point =
          res && res.adresser && res.adresser[0] && res.adresser[0].representasjonspunkt;
        const result: GeoPoint | null =
          point && point.lat && point.lon ? { lat: point.lat, lng: point.lon } : null;

        this.memory.set(key, result);
        this.inflight.delete(key);

        if (result) {
          this.persistent.set(key, { lat: result.lat, lng: result.lng, ts: Date.now() });
          this.prune();
          await this.persist();
        }
        return result;
      })
      .catch(() => {
        // Cache null kun in-memory (retry i ny økt), ikke persistent
        this.memory.set(key, null);
        this.inflight.delete(key);
        return null;
      });

    this.inflight.set(key, request);
    return request;
  }

  /** Fjerner utløpte oppføringer, deretter de eldste over MAX_ENTRIES. */
  private prune(): void {
    const now = Date.now();
    for (const [k, e] of this.persistent) {
      if (now - e.ts >= TTL_MS) {
        this.persistent.delete(k);
      }
    }
    if (this.persistent.size > MAX_ENTRIES) {
      const sorted = [...this.persistent.entries()].sort((a, b) => a[1].ts - b[1].ts);
      const toRemove = sorted.slice(0, this.persistent.size - MAX_ENTRIES);
      for (const [k] of toRemove) {
        this.persistent.delete(k);
      }
    }
  }

  private async persist(): Promise<void> {
    try {
      const obj: { [k: string]: CacheEntry } = {};
      for (const [k, e] of this.persistent) {
        obj[k] = e;
      }
      await this.storage.set(STORAGE_KEY, obj);
    } catch {
      // ignorer lagringsfeil
    }
  }
}
